import { BaseSyntheticEvent, useCallback, useState } from "react";
import { Loader } from "../../components/ui/Loader";
import { DataTable } from "primereact/datatable";
import { TableHeader } from "../../components/ui/table-header";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useToast } from "../../components/ui/toast-context-provider";
import { useQueryClient } from "react-query";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import { FilterMatchMode } from "primereact/api";
import {
  useAddTreatmentTypeMutation,
  useDeleteTreatmentTypeMutation,
  useEditTreatmentTypeMutation,
  useTreatmentTypeQuery,
} from "../../queries/veterinary-treatment-types.query";
import { TreatmentType } from "../../queries/models/treatment-type.model";
import { TreatmentTypesForm } from "./TreatmentTypesForm";

export function TreatmentTypesPage() {
  const queryClient = useQueryClient();
  const typesQuery = useTreatmentTypeQuery();
  const addTypeMutation = useAddTreatmentTypeMutation();
  const editTypeMutation = useEditTreatmentTypeMutation();
  const deleteTypeMutation = useDeleteTreatmentTypeMutation();
  const toast = useToast();
  const [selectedType, setSelectedType] = useState<TreatmentType>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleGlobalFilterChange = useCallback(
    (e: BaseSyntheticEvent) => {
      const value = e.target.value;
      let _filters = filters;
      _filters.global.value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  const handleAddNewType = () => {
    setSelectedType({
      name: "",
    } as TreatmentType);
  };

  const handleSaveType = (data: TreatmentType) => {
    return data?.id ? editExistingType(data) : saveNewType(data);
  };

  const saveNewType = (data: TreatmentType) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedType(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: TreatmentType = {
      id: data.id,
      name: data.name,
      order: data.order,
      comment: data.comment,
    };

    return addTypeMutation.mutateAsync(request, mutateOptions);
  };

  const editExistingType = (data: TreatmentType) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedType(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: TreatmentType = {
      id: data.id,
      name: data.name,
      order: data.order,
      comment: data.comment,
    };

    return editTypeMutation.mutateAsync(request, mutateOptions);
  };

  const mobileTypeFormModalProps: CustomModalProps = {
    header: selectedType?.id ? selectedType.name : "Dodaj nowy typ",
    onClose: () => {
      setSelectedType(undefined);
      setIsModalOpen(false);
    },
    isOpen: isModalOpen,
    body: (
      <TreatmentTypesForm
        veterinaryTreatmentType={selectedType ?? ({} as TreatmentType)}
        onSave={handleSaveType}
        onCancel={() => {
          setSelectedType(undefined);
          setIsModalOpen(false);
        }}
        onDelete={() => setIsDeleteConfirmationModalOpen(true)}
        isSubmitting={isSubmitting}
      />
    ),
  };

  const handleDeleteType = useCallback(() => {
    if (selectedType) {
      deleteTypeMutation.mutateAsync(selectedType.id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          toast.current?.show({
            severity: "success",
            detail: "Zapisano!",
          });
          setSelectedType(undefined);
          setIsDeleteConfirmationModalOpen(false);
          setIsModalOpen(false);
        },
        onError: async (error: any) => {
          toast.current?.show({
            severity: "error",
            detail: error.message,
          });
        },
      });
    }
  }, [deleteTypeMutation, queryClient, selectedType, toast]);

  const mobileModalConfirmationFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Czy chcesz usunąć?",
    height: "160px",
    width: "50%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDeleteType,
  };

  return (
    <>
      {typesQuery.isLoading ? (
        <div className="h-full flex align-items-center">
          <Loader type="spinner" />
        </div>
      ) : (
        <div className="h-full">
          {!!mobileTypeFormModalProps && (
            <CustomModal {...mobileTypeFormModalProps} />
          )}
          {!!mobileModalConfirmationFormProps && (
            <CustomModal {...mobileModalConfirmationFormProps} />
          )}
          <div className="p-1 ">
            <InputText
              className="w-full"
              placeholder="Filtr globalny"
              value={globalFilterValue}
              onChange={handleGlobalFilterChange}
            />
          </div>

          <div style={{ height: "calc(100% - 3rem)" }}>
            <DataTable
              value={typesQuery.data}
              resizableColumns={true}
              scrollable={true}
              scrollHeight="flex"
              selectionMode="single"
              selection={selectedType}
              onSelectionChange={(e) => {
                setSelectedType(e.value as TreatmentType);
                setIsModalOpen(true);
              }}
              filters={filters}
              globalFilterFields={["name", "comment"]}
              header={
                <TableHeader
                  header="Typy zabiegów"
                  showAddButton={true}
                  onClickAdd={() => {
                    handleAddNewType();
                    setIsModalOpen(true);
                  }}
                />
              }
            >
              <Column field="order" header="Kolejność" sortable />
              <Column field="name" header="Nazwa" sortable />
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
}
